"use client";

import useCart from "@/hooks/useCart";
import useDates from "@/hooks/useDates";
import { useAppSelector } from "@/store/hooks";
import { Button, NumberField, Typography } from "@repo/ui";
import {
  Calendar,
  Clock,
  Monitor,
  Building,
  CheckCircle,
  ChevronDown,
  Timer,
  LanguagesIcon,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { formatCurrency } from "@/lib/helpers";
import moment from "moment";
import { useCountryCode } from "@/hooks/useCountryCode";

type ScheduleCardProps = {
  data: ServiceEvent;
};

const ScheduleCard = ({ data }: ScheduleCardProps) => {
  const [showOffers, setShowOffers] = useState(true);
  const [quantity, setQuantity] = useState<number>(1);
  const { country } = useCountryCode();
  const router = useRouter();
  const status = useAppSelector((state) => state.cart.status);
  const { formattedDate, weekLabel, numberOfDays } = useDates(data.eventDates);
  const days = numberOfDays > 1 ? "Days" : "Day";

  const { addToCart } = useCart({
    onSuccess: () => {
      router.push(`/${country}/order-summary`);
    },
  });

  const hours = useMemo(() => {
    if (data?.eventDetails.startTime && data.eventDetails.endTime) {
      const startTime = moment(data?.eventDetails.startTime, "HH:mm");
      const endTime = moment(data?.eventDetails.endTime, "HH:mm");
      const hours = endTime.diff(startTime, "hours", true);

      return hours;
    } else {
      return null;
    }
  }, [data?.eventDetails.startTime, data?.eventDetails.endTime]);

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl border">
      <div className="flex flex-col lg:flex-row">
        {/* Left section with date and time */}
        <div className="bg-primary-light/10 p-6 lg:w-2/5">
          <div className="flex flex-wrap gap-2 mb-4">
            {weekLabel && (
              <span className="inline-block px-3 py-1 bg-primary-main/15 text-primary-main rounded-full text-xs font-medium transition-transform hover:scale-105">
                {weekLabel}
              </span>
            )}
            {data.isEarlyBirdOfferApplied && (
              <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium transition-transform hover:scale-105">
                early bird
              </span>
            )}
            {data.isFastFilling && (
              <span className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium transition-transform hover:scale-105">
                <Timer size={12} className="mr-1" />
                filling fast
              </span>
            )}
          </div>

          <div className="flex items-start space-x-3 mb-4">
            <Calendar
              className="text-primary-main mt-1 flex-shrink-0"
              size={18}
            />
            <div>
              <h3 className="font-semibold text-gray-900">{formattedDate}</h3>
              <p className="text-xs text-gray-600">
                {numberOfDays} {days}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Clock className="text-primary-main mt-1 flex-shrink-0" size={18} />
            <div>
              <p className="font-medium text-gray-900">
                {data.eventDetails.startTime} to {data.eventDetails.endTime}
              </p>
              <p className="text-xs text-gray-600">
                {data.eventDetails.timezone} <span>({hours} Hrs / Day)</span>
              </p>
            </div>
          </div>

          {/* Trainer information */}
          {data.eventDetails.trainer && (
            <div className="mt-3">
              <div className="flex items-center space-x-3">
                <Image
                  src={data.eventDetails.trainer.image}
                  alt={data.eventDetails.trainer.name}
                  width={96}
                  height={96}
                  aria-label={data.eventDetails.trainer.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-primary-main shadow-sm flex-shrink-0"
                />
                <div>
                  <Typography
                    render={<div />}
                    className="!font-regular !text-sm text-gray-900"
                  >
                    {data.eventDetails.trainer?.name}
                  </Typography>
                  <Typography className="!text-xs text-gray-600">
                    {data.eventDetails.trainer?.experience}
                  </Typography>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right section with training details */}
        <div className="flex-1 p-6">
          <div className="flex flex-col md:flex-row md:items-start items-stretch  md:justify-between">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center space-x-2 mb-2">
                {data.modeOfTraining === "online" ? (
                  <Monitor className="text-primary-main" size={18} />
                ) : (
                  <Building className="text-primary-main" size={18} />
                )}
                <span className="font-medium text-gray-900">
                  {data.modeOfTraining}
                </span>
              </div>

              <div className="flex items-center space-x-2 mb-2 text-green-600">
                <CheckCircle size={18} />
                <span className="text-sm font-medium">Guarantee to run</span>
              </div>

              <div className="flex items-center space-x-2 mb-2 ">
                <LanguagesIcon size={18} className="text-primary-main" />
                <span className="text-sm font-medium">English</span>
              </div>

              <div className="text-sm text-gray-700 mb-2">
                <b>Exam:</b>{" "}
                <span className="font-medium">
                  {data.withExam ? "Included" : "Not Included"}
                </span>
              </div>

              <div className="text-sm text-gray-700 mb-2">
                <b>Certificate:</b>{" "}
                <span className="font-medium">Course Completion</span>
              </div>

              {data.offers.length > 0 && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowOffers(!showOffers)}
                    className="flex items-center text-primary-main hover:text-primary-main text-sm font-medium transition-colors"
                  >
                    <ChevronDown
                      size={16}
                      className={`mr-1 transition-transform duration-200 ${
                        showOffers ? "rotate-180" : ""
                      }`}
                    />
                    {data.offers.length} Special Offers Available
                  </button>

                  {showOffers && (
                    <div className="mt-2 pl-5 border-l-2 border-blue-100">
                      {data.offers.map((offer, index) => (
                        <div
                          key={index}
                          className="text-sm text-gray-700 mb-2 last:mb-0"
                        >
                          {offer.description} - {offer.discountPercentage}%
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col md:items-end ">
              <div className="flex items-center space-x-2 mb-3">
                <NumberField
                  label="Learners:"
                  inline
                  min={1}
                  onValueChange={(value) => setQuantity(value || 1)}
                />
              </div>

              <div className="flex flex-col md:items-end mb-3">
                <div className="flex items-center mb-1">
                  <span className="text-2xl font-bold text-blue-900">
                    {formatCurrency(
                      data.eventDetails.sellingPriceNumber * quantity,
                      data.eventDetails.currency
                    )}
                  </span>
                  {data.eventDetails.price && (
                    <span className="ml-2 text-sm line-through text-gray-500">
                      {formatCurrency(
                        data.eventDetails.priceNumber * quantity,
                        data.eventDetails.currency
                      )}
                    </span>
                  )}
                </div>

                {data.eventDetails.discountValue && (
                  <span className="text-sm font-medium text-green-600">
                    Save {data.eventDetails.discountPercent} (
                    {formatCurrency(
                      data.eventDetails.discountValueNumber * quantity,
                      data.eventDetails.currency
                    )}
                    )
                  </span>
                )}
              </div>

              <div className="grid gap-2 grid-flow-row">
                <Button
                  color="primary"
                  fullWidth
                  loading={status === "loading"}
                  onClick={() =>
                    addToCart({
                      serviceId: data.serviceId,
                      eventId: data.id,
                      eventDetailId: data.eventDetails.id,
                      quantity: quantity || 1,
                    })
                  }
                >
                  Enroll now
                </Button>
                <Button
                  color="primary"
                  variant="outlined"
                  size="small"
                  href="#request-for-group-training"
                >
                  Request For Group Training
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCard;
